import React from "react";
import EntityPreviewCard from "./EntityPreviewCard";
import { typeLabel, PREVIEW_META_KEYS, SELF_EXPERT_SOURCE_TOOLTIP } from "./constants";

// Scalar preview keys whose values differ between the two sides — drives the
// diff highlight in each card.
function differingKeys(source, destination) {
  if (!source || !destination) return [];
  const keys = new Set([...Object.keys(source), ...Object.keys(destination)]);
  return [...keys].filter((key) => {
    if (PREVIEW_META_KEYS.includes(key)) return false;
    return String(source[key]) !== String(destination[key]);
  });
}

/**
 * MergePreview — step 2. Side-by-side source/destination diff plus the Expert
 * directional guard: a self-representing expert may be merged INTO (destination)
 * but never merged AWAY (source), so a self-expert source blocks the flow.
 */
function MergePreview({ preview, loading, error, onBack, onNext }) {
  if (loading) {
    return (
      <div className="text-center py-5" data-testid="preview-loading">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading preview…</span>
        </div>
        <p className="mt-3 text-muted">Loading merge preview…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div className="alert alert-danger">
          <i className="fa fa-exclamation-circle me-2" />
          {error}
        </div>
        <button type="button" className="btn btn-secondary" onClick={onBack}>
          Back
        </button>
      </div>
    );
  }

  if (!preview) return null;

  const { source, destination, type, v1_merge_enabled: v1MergeEnabled } = preview;
  const diffKeys = differingKeys(source, destination);
  const sourceIsSelfExpert = source?.self_expert === true;

  return (
    <div>
      <div className="alert alert-warning">
        <i className="fa fa-exclamation-triangle me-2" />
        <strong>Destructive operation.</strong> The <strong>source</strong> {typeLabel(type)} is permanently deleted;
        its associations move to the <strong>destination</strong>. This cannot be undone.
      </div>

      <div className="row g-3 mb-3">
        <div className="col-md-6">
          <EntityPreviewCard side="source" preview={source} differingKeys={diffKeys} />
        </div>
        <div className="col-md-6">
          <EntityPreviewCard side="destination" preview={destination} differingKeys={diffKeys} />
        </div>
      </div>

      <div className="mb-3">
        {v1MergeEnabled ? (
          <span className="badge bg-info text-dark">
            <i className="fa fa-database me-1" />
            V1 writeback enabled — both databases will be updated
          </span>
        ) : (
          <span className="badge bg-secondary">V2-only merge (V1 writeback disabled)</span>
        )}
      </div>

      {sourceIsSelfExpert && (
        <div className="alert alert-danger" data-testid="self-expert-source-block">
          <i className="fa fa-ban me-2" />
          The source is a <strong>self-representing expert</strong> and cannot be merged away.{" "}
          {SELF_EXPERT_SOURCE_TOOLTIP} Swap source and destination, or pick a different source.
        </div>
      )}

      <div className="d-flex justify-content-between">
        <button type="button" className="btn btn-secondary" onClick={onBack}>
          Back
        </button>
        {/* Wrapping span carries the tooltip even while the button is disabled. */}
        <span title={sourceIsSelfExpert ? SELF_EXPERT_SOURCE_TOOLTIP : undefined} data-testid="proceed-wrapper">
          <button
            type="button"
            className="btn btn-danger"
            onClick={onNext}
            disabled={sourceIsSelfExpert}
            data-testid="proceed-to-confirm"
          >
            Proceed to Confirm
          </button>
        </span>
      </div>
    </div>
  );
}

export default MergePreview;
